import { useAuth0 } from '@auth0/auth0-react';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, styled, Typography } from '@mui/material';
import axios from 'axios';
import 'cropperjs/dist/cropper.css';
import React, { useRef, useState } from 'react';
import Cropper from 'react-cropper';
import defaultSrc from '../../assets/user_default.png';

const Image = styled('img')(({ theme }) => ({
    borderRadius: '15px',
    boxShadow: '0 0 25px 0 #b3bac7',
    'margin-left': 'auto',
    'margin-right': 'auto',
    width: 'inherit',
}));

const ImageBox = styled(Box)(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        width: 260,
    },
    [theme.breakpoints.up('sm')]: {
        width: 400,
    },
    [theme.breakpoints.up('lg')]: {
        width: 450,
    },
}));

const MainDiv = styled('div')(({ theme }) => ({
    position: 'absolute',
    backgroundColor: 'white',
    boxShadow: theme.shadows[5],
    borderRadius: 10,
    [theme.breakpoints.down('sm')]: {
        width: '320px',
    },
    [theme.breakpoints.up('sm')]: {
        width: '500px',
    },
    [theme.breakpoints.up('lg')]: {
        width: '700px',
    },
}));

const BookingButton = styled(LoadingButton)(({ theme }) => ({
    padding: '10px 0px',
    margin: '10px auto',
    width: '100%',
    maxWidth: '150px',
    borderRadius: '30px',
}));

function getModalStyle() {
    const top = 50;
    const left = 50;

    return {
        top: `${top}%`,
        left: `${left}%`,
        transform: `translate(-${top}%, -${left}%)`,
    };
}

const InputImageModal = props => {
    const { getAccessTokenSilently } = useAuth0();
    const [modalStyle] = React.useState(getModalStyle);
    const [image, setImage] = useState(defaultSrc);
    const [state, setState] = useState({
        postFormattedURL: '',
        stage: 0,
        preFormattedURL: '',
        returnedImage: '',
        cropResult: null,
        uploadedImage: false,
        error: false,
        errorCode: '',
    });
    const [cropData, setCropData] = useState('#');
    const [cropper, setCropper] = useState();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const imageRef = useRef(null);

    const handleSubmission = () => {
        if (state.filesize > 3145730) {
            setState({
                ...state,
                error: true,
                errorCode: 'File too large. 3MB Max file size',
            });
        } else {
            setIsSubmitting(true);
            setState({
                ...state,
                error: false,
                errorCode: '',
            });
            getAccessTokenSilently({
                scope: process.env.REACT_APP_scope,
            })
                .then(function (token) {
                    return axios.put(
                        '/imageprofile',
                        cropper.getCroppedCanvas().toDataURL(state.filetype),
                        {
                            params: {
                                Filename: state.filename,
                                Type: 'ProfileImage',
                                Permission: 43,
                            },
                            baseURL: process.env.REACT_APP_API_GATEWAY,
                            timeout: 30000,
                            headers: {
                                authorization: `Bearer ${token}`,
                                'Content-Type': 'text/html',
                            },
                        }
                    );
                })
                .then(function (url) {
                    let stringURL = url.data.photoFilename;
                    console.log('URL: ' + stringURL);
                    setIsSubmitting(false);
                    props.parentCallback(stringURL);
                })
                .catch(function (err) {
                    console.log('This is the error: ' + err);
                    setIsSubmitting(false);
                    setState({
                        ...state,
                        error: true,
                        errorCode: 'Failed to upload image',
                    });
                    return err;
                });
        }
    };

    const onChange = e => {
        e.preventDefault();
        let files;
        if (e.dataTransfer) {
            files = e.dataTransfer.files;
        } else if (e.target) {
            files = e.target.files;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setImage(reader.result);
        };
        reader.readAsDataURL(files[0]);
        setState({
            ...state,
            stage: 0,
            uploadedImage: true,
            filename: files[0].name,
            filetype: files[0].type,
            filesize: files[0].size,
        });
    };

    const getCropData = () => {
        if (typeof cropper !== 'undefined') {
            setCropData(cropper.getCroppedCanvas().toDataURL(state.filetype));
            setState({
                ...state,
                stage: 1,
            });
        }
    };

    return (
        <MainDiv style={modalStyle}>
            <Box sx={{ m: 1 }}>
                <Box>
                    {state.stage === 0 && (
                        <Box>
                            <Typography variant="h2">{props.text}</Typography>
                            <BookingButton
                                variant="contained"
                                component="label"
                                sx={{ m: 1 }}
                                data-cy="profile-upload"
                            >
                                Upload File
                                <input
                                    type="file"
                                    hidden
                                    onChange={onChange}
                                    accept="image/png, image/gif, image/jpeg"
                                />
                            </BookingButton>
                            <Typography variant="subtitle2" sx={{ m: 1 }}>
                                NOTE: Pinch or scroll to scale image
                            </Typography>
                            <Cropper
                                style={{ height: 400, width: '100%' }}
                                aspectRatio={1}
                                preview=".img-preview"
                                src={image}
                                ref={imageRef}
                                viewMode={0}
                                dragMode="move"
                                scalable={true}
                                guides={true}
                                minCropBoxHeight={10}
                                minCropBoxWidth={10}
                                background={false}
                                responsive={true}
                                checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
                                onInitialized={instance => {
                                    setCropper(instance);
                                }}
                            />
                            {state.uploadedImage && (
                                <BookingButton
                                    style={{ float: 'right' }}
                                    onClick={getCropData}
                                    data-cy="image-next"
                                    variant="contained"
                                    sx={{ m: 1 }}
                                >
                                    Next
                                </BookingButton>
                            )}
                        </Box>
                    )}
                    {state.stage === 1 && (
                        <ImageBox sx={{ mx: 'auto' }}>
                            <Typography variant="h2">{props.text}</Typography>
                            <BookingButton
                                variant="contained"
                                component="label"
                                sx={{ m: 1 }}
                            >
                                Upload File
                                <input type="file" hidden onChange={onChange} />
                            </BookingButton>
                            <Image src={cropData} alt="cropped" />
                            <Box sx={{ textAlign: 'right' }}>
                                <BookingButton
                                    variant="contained"
                                    color="primary"
                                    sx={{ m: 1 }}
                                    onClick={handleSubmission}
                                    loading={isSubmitting}
                                    data-cy="image-submit"
                                >
                                    Submit
                                </BookingButton>
                                {state.error && (
                                    <Typography color="error">
                                        {state.errorCode}
                                    </Typography>
                                )}
                            </Box>
                        </ImageBox>
                    )}
                </Box>
            </Box>
        </MainDiv>
    );
};

export default InputImageModal;
